<template>
  <ValidationObserver
    ref="formValidation"
    v-slot="{ pristine, invalid }"
    slim
  >
    <form
      role="form"
      class="form-register-mfa"
      :class="{ 'login-flow': isLoginFlow }"
    >
      <h2
        v-if="isLoginFlow"
        class="m-b-15"
        v-t="'PAGE_TITLE'"
      />
      <div class="bs-callout bs-callout-sm bs-callout-primary">
        <span
          v-if="isLoginFlow"
          v-html="loginFlowMessage"
        />
        <span
          v-html="$t('NOTE_AUTH_APP_REQUIRED')"
        />
      </div>
      <div class="col-sm-12 col-md-6">
        <h3
          v-text="$t('SCAN_THIS_QRCODE_TITLE')"
          class="m-b-10"
        />
        <div>
          <div
            v-if="isLoadingSettings"
            class="m-b-15 m-t-15"
          >
            <Loader />
            <span v-t="'LOADING_QR_CODE'" />
          </div>

          <div
            v-if="!isLoadingSettings"
            class="qr-code"
          >
            <img
              style="width:100%"
              :src="qrCodeImageSource"
              alt=""
            >
          </div>
        </div>
        <div class="m-b-15">
          <span
            v-html="$t('SCAN_THIS_QRCODE_DESCRIPTION.NO_CAMERA_INSTRUCTION')"
          />
          <span
            v-html="$t('SCAN_THIS_QRCODE_DESCRIPTION.ENTER_CODE_APP')"
          />
          <span
            class="clickable"
            @click="onCopySecretClick"
          >
            {{ mfaSettings.secretKey }} <i class="fa fa-copy"></i>
          </span>
        </div>
      </div>
      <div class="col-sm-12 col-md-6">
        <h3
          v-text="$t('ENTER_VALIDATION_DIGITS_TITLE')"
          class="m-b-10"
        />
        <Field
          :label="$t('VALIDATION_CODE')"
          :has-tooltip="true"
          :tooltip-text="$t('VALIDATION_CODE_TOOLTIP')"
        >
          <template #input>
            <ValidationProvider
              rules="required"
              slim
            >
              <input
                v-model="sixDigitsValidation"
                ref="validationInput"
                type="text"
                class="form-control r-form__input width-auto p-10"
                :class="{ 'error': enableMFAStatus.error }"
                required="true"
              />
              <div
                class="validation-error"
              >
                <span
                  v-if="enableMFAStatus.error"
                  class="text-danger"
                >
                  {{ enableMFAStatus.error }}
                </span>
              </div>
            </ValidationProvider>
          </template>
        </Field>
        <PrimaryButton
          size="sm"
          :disabled="pristine || invalid"
          :show-loader-on-form="enableMFAStatus.status === 'pending'"
          @click="onEnableMFA"
        >
          {{ $t('ENABLE') }}
        </PrimaryButton>

        <button
          v-if="!isLoginFlow"
          v-t="'CANCEL'"
          class="btn btn-sm btn-danger m-l-15"
          @click="hideDetailsForm"
        />
        <p
          v-if="isLoginFlow && !enforceMfa"
          class="m-t-15 m-b-15"
        >
          <span
            v-t="'SKIP_THIS_TIME'"
            class="clickable"
            @click="skipMFA"
          />
        </p>
      </div>

      
    </form>
  </ValidationObserver>
</template>

<script>
import moment from 'moment';
import messages from './locales';
import sharedMessages from '@/core/locales';
import { mapActions, mapGetters, mapState } from 'vuex';
import Field from '@/core/components/Form/BaseFormFieldV2';
import Loader from '@/core/components/Loader';
import PrimaryButton from '@/core/components/PrimaryButton';
import copy from 'copy-to-clipboard';
import { utilities, cacheService } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import { STATUS_PENDING, STATUS_SUCCESS, STATUS_ERROR, getStatus } from '@/store/helpers';

export default {
  name: 'TwoFactorAuthMFAForm',

  components: {
    Field,
    PrimaryButton,
    Loader
  },
  props: {
    isLoginFlow: {
      type: Boolean,
      default: false
    },
    enforceMfa: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      sixDigitsValidation: '',
    };
  },

  computed: {
    ...mapState(['timezones']),
    ...mapState('twoFactorAuth', {
      mfaSettingsStatus: (state) => getStatus(state, 'getMFASettings'),
      enableMFAStatus: state => getStatus(state, 'enableMFA')
    }),
    ...mapState('twoFactorAuth', [
      'mfaSettings',
    ]),
    ...mapGetters(['accountTranslatedTimeZoneId']),

    // Uses the account timezone to translate the UTC MFA enforce date to the user's time
    accountMFAEnforceDate() {
      const authData = cacheService.get('authService.authorizationData');
      const mfaEnforceDate = moment.utc(authData?.mfaEnforceDate);
      const timezone = this.accountTranslatedTimeZoneId || moment.tz.guess();
      return moment(mfaEnforceDate).tz(timezone).format('MMMM DD, YYYY');
    },

    isLoadingSettings() {
      return this.mfaSettingsStatus.status == STATUS_PENDING;
    },

    qrCodeImageSource() {
      if (!this.mfaSettings) {
        return;
      }

      return `http://qrcode.kaywa.com/img.php?s=4&d=${this.mfaSettings.barcodeUrl}`;
    },
    loginFlowMessage() {
      return !this.enforceMfa ? this.$t('NOTE_MFA_NOW_REQUIRED_DATE', {enforceDate: this.accountMFAEnforceDate}) : this.$t('NOTE_MFA_NOW_REQUIRED');
    }
  },

  created() {
    this.getMFASettings();
    // Use $nextTick to ensure the DOM is updated before focusing
    this.$nextTick(() => {
      this.$refs['validationInput'].focus();
    });
  },

  methods: {
    ...mapActions('twoFactorAuth', [
      'getMFASettings', 
      'enableMFA'
    ]),

    onCopySecretClick() {
      if (!this.mfaSettings) {
        return;
      }

      copy(this.mfaSettings.secretKey);
      utilities.showToaster('success', this.$i18n.t('SECRET_HAS_BEEN_COPIED_TO_CLIPBOARD'));
    },

    hideDetailsForm() {
      this.$emit('hideDetailsForm');
    },

    async onEnableMFA() {
      await this.enableMFA({
        secretKey: this.mfaSettings.secretKey,
        code: this.sixDigitsValidation
      });

      this.handleActionStatus(this.enableMFAStatus, 'MFA_ENABLED_SUCCESS');
    },

    handleActionStatus(action, successMessageKey) {
      if (action.status === STATUS_SUCCESS) {
        this.hideDetailsForm();

        // Emit an event to AngularJS's $rootScope to notify that MFA has been enabled for the banner to consume
        const $rootScope = angular.element(document.body).injector().get('$rootScope');
        $rootScope.$emit('mfaEnabledSuccess');

        if (this.isLoginFlow) {
          this.$emit('redirect-to-dashboard');
        }

        // Wrap this in a timeout to allow the form to close and if login flow, after the redirect.
        setTimeout(() => {
          utilities.showToaster(STATUS_SUCCESS, this.$i18n.t(successMessageKey));
        }, 600);
      }
      if (action.status === STATUS_ERROR) {
        this.sixDigitsValidation = '';
        this.$refs['validationInput'].focus();
      }
    },
    skipMFA() {
      this.$emit('redirect-to-dashboard');
    }
  },

  i18n: {
    messages,
    sharedMessages,
    locale: 'en'
  }
};
</script>

<style lang="scss" scoped>
.form-register-mfa {
  margin: 0 auto;
  width: 75%;
  color: var(--reset-body-font-color);
  h2 {
    font-size: 1.25rem;
  }
  h3 {
    font-size: 1rem;
  }
  span {
    line-height: 1.6;
  }
  .copy-span {
    color: #58a6ff;
  }
  .copy-span:hover {
    text-decoration: underline;
  }
  .qr-code {
    height: 150px;
    width: 150px;
    margin: 15px 0;;
  }
  .r-form__input {
    .form-control {
      background: transparent !important;
      border: 1px solid var(--reset-body-font-color);
      height: auto;
      &.error {
      border: 1px solid var(--component-form__error--red) !important;
      } 
    }
    .validation-error {
      height: 1rem;
    }
  }
  &.login-flow {
    .btn-primary {
      border-radius: 1rem;
    }
  }
}
</style>